import Model, { attr, belongsTo, hasMany } from "@ember-data/model";
import { service } from "@ember/service";
import type { IntlService } from "ember-intl";
import type BioExamOrder from "./bio-exam-order";
import type LoincQualitativeValue from "./loinc-qualitative-value";

type ValueType = "number" | "string";
export type Specimen = "BLD" | "UR";

export default class Loinc extends Model {
  @service declare intl: IntlService;
  // Content coming from JDVCircuitBio

  @belongsTo("bio-exam-order", {
    async: false,
    inverse: "bioExamObservations"
  })
  declare bioExamOrder: BioExamOrder;
  // Other content coming from Sêmeia
  @attr("string") declare customLabel: string;
  @attr("string") declare defaultUnit: string;
  @attr("string") declare label: string;
  /**
   * This is also the primary key / id of the record.
   * Thus it can be used to get a record from the LOINC code:
   *
   * ```js
   * this.store.peekRecord("loinc", "12345-5");
   * ```
   */
  @attr("string") declare loinc: string;
  @attr("string") declare loincChapterLabel: string;
  @hasMany("loinc-qualitative-value", {
    async: false,
    inverse: "loinc"
  })
  declare loincQualitativeValues: LoincQualitativeValue[];
  @attr("number") declare maxReferenceValue: number;
  @attr("number") declare minReferenceValue: number;
  @attr("string") declare placeholder: string;
  @attr("number") declare rank: number;
  @attr("string") declare recommendedUnit: string;
  @attr("string") declare referenceValuesMessage: string;
  @attr("string") declare specimen: Specimen;
  @attr("string") declare valueType: ValueType;
  @attr("boolean") declare zeroable: boolean;

  get displayLabel(): string {
    return this.customLabel || this.label;
  }

  get labelWithSpecimen() {
    if (!this.specimen) {
      return this.label;
    }

    return `${this.label} (${this.intl.t("bio-exam-order.specimen." + this.specimen)})`;
  }

  // There are two source of units:
  // -  `recommended_unit`, which comes from JDVCircuitBio,
  // -  `default_unit`, which is from us and let us override or define another unit.
  //
  // `default_unit` has the priority over `recommended_unit`.
  get unit(): string {
    return this.defaultUnit || this.recommendedUnit;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    loinc: Loinc;
  }
}
